import React, { useRef, useEffect, useCallback } from 'react';

interface ParticleConfig {
  imageUrl?: string;
  shape?: 'circle' | 'square' | 'image';
  count: number;
  sizeMin: number;
  sizeMax: number;
  opacityMin: number;
  opacityMax: number;
  rotationMin: number;
  rotationMax: number;
  color?: string;
  speedMin: number;
  speedMax: number;
  direction: number;
  spread: number;
  opacityAnimation: boolean;
  opacityAnimSpeed: number;
  wobble: boolean;
  wobbleAmount: number;
  wobbleSpeed: number;
  loop: boolean;
  lifespan?: number;
  emitMode?: 'area' | 'point' | 'converge' | 'orbit';
}

interface ParticleState {
  x: number;
  y: number;
  size: number;
  opacity: number;
  rotation: number;
  speed: number;
  directionRad: number;
  wobbleOffset: number;
  opacityPhase: number;
  age: number;
  lifespan: number;
  orbitAngle: number;
  orbitRadius: number;
  orbitSpeed: number;
}

interface ParticleCanvasProps {
  config: ParticleConfig;
  width: number;
  height: number;
  className?: string;
  style?: React.CSSProperties;
}

const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);

const createParticle = (config: ParticleConfig, width: number, height: number): ParticleState => {
  const mode = config.emitMode || 'area';
  const cx = width / 2;
  const cy = height / 2;
  const fullRadius = Math.min(width, height) / 2;
  const radiusPercent = ((config as any).radius ?? 80) / 100;
  const maxRadius = fullRadius * radiusPercent;
  const dirDeg = config.direction + randomBetween(-config.spread / 2, config.spread / 2);
  const dirRad = (dirDeg * Math.PI) / 180;

  let x: number, y: number;
  // Pour point/converge : utiliser direction + spread au lieu de 360° complet
  const useDirectionalSpawn = (mode === 'point' || mode === 'converge') && config.spread < 360;
  const orbitAngle = useDirectionalSpawn
    ? ((config.direction + randomBetween(-config.spread / 2, config.spread / 2)) * Math.PI) / 180
    : Math.random() * Math.PI * 2;
  const orbitRadius = randomBetween(maxRadius * 0.2, maxRadius);
  const orbitSpeed = randomBetween(config.speedMin, config.speedMax) / 100;

  const staggerAmount = ((config as any).stagger ?? 100) / 100;
  const progress = Math.random() * staggerAmount;

  switch (mode) {
    case 'point':
      x = cx + Math.cos(orbitAngle) * maxRadius * progress;
      y = cy + Math.sin(orbitAngle) * maxRadius * progress;
      break;
    case 'converge':
      x = cx + Math.cos(orbitAngle) * maxRadius * (1 - progress);
      y = cy + Math.sin(orbitAngle) * maxRadius * (1 - progress);
      break;
    case 'orbit':
      x = cx + Math.cos(orbitAngle) * orbitRadius;
      y = cy + Math.sin(orbitAngle) * orbitRadius;
      break;
    default: x = Math.random() * width; y = Math.random() * height;
  }

  return {
    x, y,
    size: randomBetween(config.sizeMin, config.sizeMax),
    opacity: randomBetween(config.opacityMin, config.opacityMax),
    rotation: randomBetween(config.rotationMin, config.rotationMax),
    speed: randomBetween(config.speedMin, config.speedMax),
    directionRad: mode === 'point' ? orbitAngle : mode === 'converge' ? orbitAngle + Math.PI : dirRad,
    wobbleOffset: Math.random() * Math.PI * 2,
    opacityPhase: Math.random() * Math.PI * 2,
    age: 0,
    lifespan: config.lifespan || 0,
    orbitAngle, orbitRadius, orbitSpeed,
  };
};

const ParticleCanvas: React.FC<ParticleCanvasProps> = ({ config, width, height, className, style }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<ParticleState[]>([]);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const imageLoadedRef = useRef(false);
  const imageRatioRef = useRef(1);
  const animFrameRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  useEffect(() => {
    if (config.imageUrl && config.shape === 'image') {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { imageRef.current = img; imageLoadedRef.current = true; imageRatioRef.current = img.width / img.height; };
      img.src = config.imageUrl;
    } else {
      imageRef.current = null;
      imageLoadedRef.current = false;
    }
  }, [config.imageUrl, config.shape]);

  useEffect(() => {
    const count = Math.min(config.count, 200); // Mobile limit
    particlesRef.current = Array.from({ length: count }, () => createParticle(config, width, height));
  }, [config, width, height]);

  const animate = useCallback((timestamp: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dt = lastTimeRef.current ? (timestamp - lastTimeRef.current) / 1000 : 0.016;
    lastTimeRef.current = timestamp;
    ctx.clearRect(0, 0, width, height);

    const particles = particlesRef.current;
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      const mode = config.emitMode || 'area';
      const cx = width / 2;
      const cy = height / 2;

      if (mode === 'orbit') {
        p.orbitAngle += p.orbitSpeed * dt;
        p.x = cx + Math.cos(p.orbitAngle) * p.orbitRadius;
        p.y = cy + Math.sin(p.orbitAngle) * p.orbitRadius;
      } else if (p.speed > 0) {
        p.x += Math.cos(p.directionRad) * p.speed * dt;
        p.y += Math.sin(p.directionRad) * p.speed * dt;
      }

      let wobbleX = 0;
      if (config.wobble) {
        p.wobbleOffset += config.wobbleSpeed * dt;
        wobbleX = Math.sin(p.wobbleOffset) * config.wobbleAmount;
      }

      let currentOpacity = p.opacity;
      if (config.opacityAnimation) {
        p.opacityPhase += config.opacityAnimSpeed * dt;
        const osc = (Math.sin(p.opacityPhase) + 1) / 2;
        currentOpacity = config.opacityMin + osc * (config.opacityMax - config.opacityMin);
      }

      const fullRadius = Math.min(width, height) / 2;
      const radiusPercent = ((config as any).radius ?? 80) / 100;
      const maxRadius = fullRadius * radiusPercent;

      if (mode === 'converge' && config.loop) {
        const dist = Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2);
        if (dist < p.size * 2) { particles[i] = createParticle(config, width, height); continue; }
      }
      if (mode === 'point' && config.loop) {
        const dist = Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2);
        if (dist > maxRadius) { particles[i] = createParticle(config, width, height); continue; }
      }

      if (p.lifespan > 0) {
        p.age += dt * 1000;
        if (p.age > p.lifespan) {
          if (config.loop) { particles[i] = createParticle(config, width, height); }
          continue;
        }
      }

      if (config.loop && p.speed > 0) {
        const m = p.size * 2;
        if (p.y > height + m) { p.y = -m; p.x = Math.random() * width; }
        if (p.y < -m) { p.y = height + m; p.x = Math.random() * width; }
        if (p.x > width + m) { p.x = -m; }
        if (p.x < -m) { p.x = width + m; }
      }

      // Edge fade
      const drawX = p.x + wobbleX;
      const drawY = p.y;
      const fadeZone = Math.min(width, height) * 0.1;
      let edgeFade = 1;
      if (fadeZone > 0) {
        const minDist = Math.min(drawX, width - drawX, drawY, height - drawY);
        if (minDist < fadeZone) edgeFade = Math.max(0, minDist / fadeZone);
      }
      // Distance fade for point/converge
      if (mode === 'point' || mode === 'converge') {
        const distToCenter = Math.sqrt((drawX - cx) ** 2 + (drawY - cy) ** 2);
        const fadeStart = maxRadius * 0.7;
        if (mode === 'point' && distToCenter > fadeStart) {
          edgeFade *= Math.max(0, 1 - (distToCenter - fadeStart) / (maxRadius - fadeStart));
        }
        if (mode === 'converge' && distToCenter < maxRadius * 0.15) {
          edgeFade *= Math.max(0, distToCenter / (maxRadius * 0.15));
        }
      }

      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, currentOpacity * edgeFade));
      ctx.translate(drawX, drawY);
      ctx.rotate((p.rotation * Math.PI) / 180);

      if (config.shape === 'image' && imageRef.current && imageLoadedRef.current) {
        const ratio = imageRatioRef.current;
        const drawW = ratio >= 1 ? p.size : p.size * ratio;
        const drawH = ratio >= 1 ? p.size / ratio : p.size;
        ctx.drawImage(imageRef.current, -drawW / 2, -drawH / 2, drawW, drawH);
      } else if (config.shape === 'circle' || !config.shape) {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fillStyle = config.color || '#ffffff';
        ctx.fill();
      } else {
        ctx.fillStyle = config.color || '#ffffff';
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
      }
      ctx.restore();
    }

    animFrameRef.current = requestAnimationFrame(animate);
  }, [config, width, height]);

  useEffect(() => {
    lastTimeRef.current = 0;
    animFrameRef.current = requestAnimationFrame(animate);
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, [animate]);

  return (
    <canvas ref={canvasRef} width={width} height={height} className={className}
      style={{ pointerEvents: 'none', ...style }} />
  );
};

export default ParticleCanvas;
